// «Allegati» in Nuova richiesta (tela, artboard 24): documenti e immagini, trascinati o scelti.
// Ogni file si carica subito e aspetta la richiesta; inviata, finisce in docs/ dello spazio.
import { useEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { ApiError, api } from '../lib/api';
import type { Attachment } from '../lib/api';
import { Icon } from '../components/Icon';

type Item = { key: string; name: string; bytes: number; id?: string; error?: string };

function size(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function AttachmentsField({
  project,
  maxMb,
  onChange,
}: {
  project: string;
  maxMb: number;
  onChange: (ready: Attachment[], uploading: boolean) => void;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // il genitore sa quali sono pronti e se qualcuno si sta ancora caricando
  useEffect(() => {
    onChange(
      items.filter((i) => i.id).map((i) => ({ id: i.id ?? '', name: i.name, bytes: i.bytes })),
      items.some((i) => !i.id && !i.error),
    );
  }, [items, onChange]);

  const patch = (key: string, change: Partial<Item>) =>
    setItems((list) => list.map((i) => (i.key === key ? { ...i, ...change } : i)));

  const add = (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files)) {
      const key = `${file.name}-${file.size}-${Math.random()}`;
      if (file.size > maxMb * 1024 * 1024) {
        setItems((list) => [
          ...list,
          { key, name: file.name, bytes: file.size, error: `oltre ${maxMb} MB` },
        ]);
        continue;
      }
      setItems((list) => [...list, { key, name: file.name, bytes: file.size }]);
      api.uploadAttachment(file, project).then(
        (a) => patch(key, { id: a.id, name: a.name }),
        (e: unknown) =>
          patch(key, { error: e instanceof ApiError && e.detail ? e.detail : 'non caricato' }),
      );
    }
  };

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    add(e.dataTransfer.files);
  };

  return (
    <div>
      <div className="t-caption dim" style={{ marginBottom: 6 }}>
        Allegati
      </div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
        style={{
          border: `1px dashed ${over ? 'var(--accent)' : 'var(--border-strong)'}`,
          background: over ? 'var(--accent-soft)' : 'transparent',
          borderRadius: 'var(--r-md)',
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Icon name="paperclip" size={16} />
        <span className="t-caption dim">
          Trascina qui documenti e immagini, o{' '}
          <button
            type="button"
            onClick={() => input.current?.click()}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              color: 'var(--accent)',
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            scegli i file
          </button>
          . Vanno in <code>docs/</code>
          {project ? ' del progetto' : ' della sessione'}, fino a {maxMb} MB ciascuno.
        </span>
        <input
          ref={input}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {items.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            margin: '8px 0 0',
            padding: 0,
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          {items.map((i) => (
            <li
              key={i.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 4px 4px 10px',
                border: `1px solid ${i.error ? 'var(--danger)' : 'var(--border)'}`,
                borderRadius: 'var(--r-full)',
                background: 'var(--panel-2)',
                maxWidth: '100%',
              }}
            >
              <span className="t-mono-xs trunc">{i.name}</span>
              <span
                className="t-caption"
                style={{ color: i.error ? 'var(--danger)' : 'var(--faint)' }}
              >
                {i.error ?? (i.id ? size(i.bytes) : 'carico…')}
              </span>
              <button
                type="button"
                aria-label={`Togli ${i.name}`}
                onClick={() => setItems((list) => list.filter((x) => x.key !== i.key))}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--dim)',
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                  width: 24,
                  height: 24,
                }}
              >
                <Icon name="x" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
